// client/src/components/pages/Geofencing/index.tsx (converted from .js, ST-00216)
//
// The Geofences page (/geofencing, sidebar "LexiStar · Assure & configure"): Zones, Events,
// Analytics and Aerials. The live map that used to be its first tab is the Live View page now
// (/lexistar/live, ST-00210); this page opens on Zones.
//
// The tab is in the address (?tab=zones|events|analytics|aerials; Zones when absent), so other
// pages can link straight to one: Live View's alert cards open ?tab=events (Pending Alerts with
// &show=open). ?tab=map or ?tab=live — the old live map — goes to Live View. The Events tab
// keeps its Show filter in the address (EVENTS_QUERY_KEYS); leaving the tab drops it.
//
// ST-00318: the Trips tab moved to Replay (/lexistar/replay?tab=trips), where a trip plays in
// Replay itself. ?tab=trips — bookmarks, old links — forwards there with the Trips parameters
// (vehicle, from, to, trip, hide, hideVehicle); a `trip` lands on that tab with the trip selected.
//
// ST-00269: Events, Analytics and the Pending Alerts / Events Today cards are LexiStar's zone
// events and visits (the legacy geofence evaluator is retired).
import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import GeofenceManager from './GeofenceManager';
import GeofenceEventsFeed from './GeofenceEventsFeed';
import GeofenceStatsCards from './GeofenceStatsCards';
import DailyTimeBreakdown from './DailyTimeBreakdown';
import AerialOverlayManager from './AerialOverlayManager';
import useTrackingFeed from './useTrackingFeed';
import {
  DEFAULT_GEOFENCING_TAB,
  EVENTS_QUERY_KEYS,
  GEOFENCING_TABS,
  LEGACY_TRIPS_TAB,
  LIVE_TAB_PARAMS,
  LIVE_VIEW_PATH,
  OPEN_ALERTS_PATH,
  isGeofencingTab,
  type GeofencingTab,
} from './geofencingPaths';
import { tripsTabPath } from '../LexiStar/Replay/replayPaths';
import { isZoneEventShow } from './zoneEvents';
import {
  FaMapMarkerAlt,
  FaDrawPolygon,
  FaBell,
  FaChartBar,
  FaImage,
  FaSatelliteDish,
} from 'react-icons/fa';
import { PageHeader } from '../../../types/commonComponents';

const TAB_META: Record<GeofencingTab, { label: string; icon: ReactNode }> = {
  zones: { label: 'Zones', icon: <FaDrawPolygon /> },
  events: { label: 'Events', icon: <FaBell /> },
  analytics: { label: 'Analytics', icon: <FaChartBar /> },
  aerials: { label: 'Aerials', icon: <FaImage /> },
};

/**
 * /geofencing: the old Live Map tab's address goes to Live View, the old Trips tab's to Replay's
 * Trips tab with its parameters (ST-00318), everything else is the page.
 */
export default function Geofencing() {
  const [params] = useSearchParams();
  const tabParam = params.get('tab');
  if (tabParam !== null && LIVE_TAB_PARAMS.has(tabParam)) {
    return <Navigate to={LIVE_VIEW_PATH} replace />;
  }
  if (tabParam === LEGACY_TRIPS_TAB) {
    return <Navigate to={tripsTabPath(params)} replace />;
  }
  return <GeofencesPage />;
}

function GeofencesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tabParam = params.get('tab');
  // An unknown ?tab= value shows Zones.
  const activeTab: GeofencingTab = isGeofencingTab(tabParam) ? tabParam : DEFAULT_GEOFENCING_TAB;
  const showParam = params.get('show');
  const eventsShow = isZoneEventShow(showParam) ? showParam : 'all';

  const {
    geofences,
    geofenceVersioning,
    events,
    unacknowledgedCount,
    eventsToday,
    loading,
    loadedOnce,
    refresh,
    acknowledge,
    acknowledgeAll,
    acknowledgedHint,
  } = useTrackingFeed({ live: false });

  const selectTab = useCallback(
    (tab: GeofencingTab) => {
      const next = new URLSearchParams(params);
      if (tab === DEFAULT_GEOFENCING_TAB) next.delete('tab');
      else next.set('tab', tab);
      if (tab !== 'events') EVENTS_QUERY_KEYS.forEach((key) => next.delete(key));
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaMapMarkerAlt}
        title="Geofences"
        subtitle="Zones, geofence events and drone imagery for LexiStar"
        actions={
          <Link
            to={LIVE_VIEW_PATH}
            aria-label="Open Live View"
            title="Open Live View"
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <FaSatelliteDish className="w-4 h-4 text-slate-500" aria-hidden="true" />
            <span className="page-header__label">Live View</span>
          </Link>
        }
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {loading && !loadedOnce && (
          <div role="status" className="flex items-center gap-2 mb-4 text-sm text-slate-500">
            <span
              className="inline-block w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
            Loading zones and events...
          </div>
        )}

        {/* Stats Cards — every zone (the map's layer chips are Live View's). Pending Alerts
            opens the Events tab on Open alerts. */}
        <GeofenceStatsCards
          stats={['geofences', 'unacknowledged', 'eventsToday']}
          geofenceCount={geofences.length}
          unacknowledgedCount={unacknowledgedCount}
          eventsToday={eventsToday}
          links={{ unacknowledged: OPEN_ALERTS_PATH }}
        />

        {/* Tabs */}
        <div
          className="flex border-b border-gray-200 mt-4 mb-4 overflow-x-auto"
          role="tablist"
          aria-label="Geofences"
        >
          {GEOFENCING_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => selectTab(tab)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {TAB_META[tab].icon}
              {TAB_META[tab].label}
              {tab === 'events' && unacknowledgedCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full tabular-nums">
                  {unacknowledgedCount > 99 ? '99+' : unacknowledgedCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[500px]">
          {/* Management shows ALL zones — layer toggles are a map-display
            concern and must never hide zones from CRUD */}
          {activeTab === 'zones' && (
            <GeofenceManager
              geofences={geofences}
              onChange={refresh}
              versioning={geofenceVersioning.versioning}
              versionHistory={geofenceVersioning.versionHistory}
              alertSwitch={geofenceVersioning.alertSwitch}
            />
          )}
          {activeTab === 'events' && (
            <GeofenceEventsFeed
              geofences={geofences}
              liveEvents={events}
              onAcknowledge={acknowledge}
              onAcknowledgeAll={acknowledgeAll}
              acknowledgedHint={acknowledgedHint}
              initialShow={eventsShow}
            />
          )}
          {activeTab === 'analytics' && <DailyTimeBreakdown />}
          {/* No onOverlayChange: the map that showed the overlays is Live View's now, and it
            loads them when it opens. */}
          {activeTab === 'aerials' && (
            <AerialOverlayManager
              geofences={geofences}
              onReviewZones={() => navigate(LIVE_VIEW_PATH)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
